import { useCallback, useEffect, useRef, useState } from 'react';
import { requestTyped } from '@xdocs/ws-protocol';
import { errorAlert } from '@xdocs/schemas';
import type { ManuscriptJsonb } from '@xdocs/schemas';
import { useWs } from '../../ws/WsProvider.js';
import { clearStoredEditorDraft, getStoredEditorDraft, setStoredEditorDraft } from './editorDraftDb.js';
import type { StoredEditorDraft } from './editorDraftDb.js';

/** F67 §28.1 autosave lifecycle vocabulary — reuses the §0.1/§8.4 states,
 * plus `conflict` (F67's own named state) and `offline-pending` (E20 §5.10-1:
 * "disconnected status" while local edits keep accumulating safely). */
export type AutosaveStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'offline-pending' | 'error' | 'conflict';

const SAVE_DEBOUNCE_MS = 1500;

export interface UseEditorAutosaveResult {
  content: ManuscriptJsonb;
  /** Call on every local edit — debounced + coalesced (§28.1), mirrored to
   * the IndexedDB draft store immediately so a crash never loses it. */
  setContent: (next: ManuscriptJsonb) => void;
  status: AutosaveStatus;
  lastSavedAt: Date | null;
  errorMessage: string | null;
  /** The version the NEXT save branches from (DAG parent). */
  baseVersionId: string;
  /** Flushes the debounce immediately (e.g. a manual "Retry" click). */
  saveNow: () => void;
  /** Reinitializes the hook after the caller reloaded the document's latest
   * version post-`conflict` (F67: "conflict banner on CONFLICT w/ reload
   * path") — clears conflict/dirty/local-draft state. */
  resetContent: (versionId: string, content: ManuscriptJsonb) => void;
  /** A local draft found on mount that hasn't been confirmed saved to the
   * server (E20 §5.10-3) — `null` once resolved (restored, discarded, or
   * none existed). Surfaced so the caller can render the "restore your
   * draft?" prompt naming the document; this hook never silently applies it. */
  restoreAvailable: StoredEditorDraft | null;
  restoreDraft: () => void;
  discardDraft: () => void;
}

/**
 * F67 autosave (§28.1): debounced `editor.save` over WS, idempotent via a
 * per-batch `clientMutationId` (a retry of the SAME dirty batch reuses it;
 * a new edit after settling generates a fresh one — matches
 * `editorSaveInput.clientMutationId`'s documented contract exactly). Offline/
 * disconnected edits stay `dirty` + mirrored to IndexedDB and retry
 * automatically the moment the socket reconnects — never lost, never
 * double-applied (E20). A `CONFLICT` reply (someone else saved past this
 * hook's `baseVersionId`) freezes further saves until the caller reloads via
 * {@link UseEditorAutosaveResult.resetContent}.
 */
export function useEditorAutosave(
  documentId: string,
  initialVersionId: string,
  initialContent: ManuscriptJsonb,
): UseEditorAutosaveResult {
  const { wsClient, connectionState } = useWs();

  const [content, setContentState] = useState(initialContent);
  const [baseVersionId, setBaseVersionId] = useState(initialVersionId);
  const [status, setStatus] = useState<AutosaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [restoreAvailable, setRestoreAvailable] = useState<StoredEditorDraft | null>(null);

  const contentRef = useRef(content);
  contentRef.current = content;
  const baseVersionIdRef = useRef(baseVersionId);
  baseVersionIdRef.current = baseVersionId;
  const statusRef = useRef(status);
  statusRef.current = status;
  const mutationIdRef = useRef<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef(false);

  const clearDebounce = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, []);

  const attemptSave = useCallback((): void => {
    clearDebounce();
    if (statusRef.current === 'conflict' || inFlightRef.current) return;
    if (connectionState !== 'connected') {
      setStatus('offline-pending');
      return;
    }
    if (!mutationIdRef.current) mutationIdRef.current = crypto.randomUUID();
    const clientMutationId = mutationIdRef.current;
    const snapshotContent = contentRef.current;
    const snapshotBase = baseVersionIdRef.current;

    inFlightRef.current = true;
    setStatus('saving');
    setErrorMessage(null);
    requestTyped(wsClient, 'editor.save', {
      documentId,
      baseVersionId: snapshotBase,
      content: snapshotContent,
      clientMutationId,
    })
      .then((version) => {
        inFlightRef.current = false;
        mutationIdRef.current = null;
        setBaseVersionId(version.id);
        setStatus('saved');
        setLastSavedAt(new Date());
        void clearStoredEditorDraft(documentId);
        // A further edit may have landed while this save was in flight —
        // re-run against the now-current base so it isn't stranded `dirty`.
        if (contentRef.current !== snapshotContent) {
          debounceRef.current = setTimeout(attemptSave, 0);
        }
      })
      .catch((err: unknown) => {
        inFlightRef.current = false;
        const parsed = errorAlert.safeParse(err);
        if (parsed.success && parsed.data.code === 'CONFLICT') {
          setStatus('conflict');
          setErrorMessage(parsed.data.message);
          return;
        }
        if (parsed.success && parsed.data.code === 'INTERNAL' && parsed.data.message === 'errors:ws.not_connected') {
          setStatus('offline-pending');
          return;
        }
        setStatus('error');
        setErrorMessage(parsed.success ? parsed.data.message : 'errors:code.INTERNAL');
      });
    // Refs carry the latest content/base/status; only `connectionState`/
    // `wsClient`/`documentId` identity changes should re-create this callback.
  }, [wsClient, connectionState, documentId, clearDebounce]);

  const setContent = useCallback(
    (next: ManuscriptJsonb) => {
      setContentState(next);
      setStatus((prev) => (prev === 'conflict' ? prev : 'dirty'));
      void setStoredEditorDraft({
        documentId,
        baseVersionId: baseVersionIdRef.current,
        content: next,
        updatedAt: new Date().toISOString(),
      });
      if (statusRef.current === 'conflict') return;
      clearDebounce();
      debounceRef.current = setTimeout(attemptSave, SAVE_DEBOUNCE_MS);
    },
    [documentId, clearDebounce, attemptSave],
  );

  const saveNow = useCallback(() => {
    attemptSave();
  }, [attemptSave]);

  const resetContent = useCallback(
    (versionId: string, next: ManuscriptJsonb) => {
      clearDebounce();
      inFlightRef.current = false;
      mutationIdRef.current = null;
      setContentState(next);
      setBaseVersionId(versionId);
      setStatus('idle');
      setErrorMessage(null);
      setLastSavedAt(null);
      void clearStoredEditorDraft(documentId);
    },
    [documentId, clearDebounce],
  );

  // Retry automatically the moment the socket comes back (E20 §5.10-1) —
  // never require the user to notice and manually retry a connectivity blip.
  useEffect(() => {
    if (connectionState === 'connected' && (statusRef.current === 'offline-pending' || statusRef.current === 'dirty')) {
      attemptSave();
    }
    // Only the transition INTO 'connected' should trigger a retry — `attemptSave` intentionally excluded (it's stable via refs, see above).
  }, [connectionState]);

  // On mount (or a document switch), check for an un-synced local draft
  // (E20 §5.10-3) — offered, never silently applied.
  useEffect(() => {
    let cancelled = false;
    setRestoreAvailable(null);
    void getStoredEditorDraft(documentId).then((stored) => {
      if (!cancelled && stored) setRestoreAvailable(stored);
    });
    return () => {
      cancelled = true;
    };
  }, [documentId]);

  useEffect(() => clearDebounce, [clearDebounce]);

  const restoreDraft = useCallback(() => {
    if (!restoreAvailable) return;
    // Rebase the recovered CONTENT onto the CURRENT `baseVersionId`
    // (`initialVersionId`/whatever `resetContent` last set it to), never the
    // stale one stored alongside the local draft — this hook is mounted
    // exactly after a conflict→reload, so `baseVersionId` already holds the
    // fresh version id; overwriting it from the draft would rebase the
    // restored edit onto an already-superseded version and immediately
    // re-trigger CONFLICT on the very next save.
    setContentState(restoreAvailable.content);
    setStatus('dirty');
    setRestoreAvailable(null);
    clearDebounce();
    debounceRef.current = setTimeout(attemptSave, SAVE_DEBOUNCE_MS);
  }, [restoreAvailable, clearDebounce, attemptSave]);

  const discardDraft = useCallback(() => {
    void clearStoredEditorDraft(documentId);
    setRestoreAvailable(null);
  }, [documentId]);

  return {
    content,
    setContent,
    status,
    lastSavedAt,
    errorMessage,
    baseVersionId,
    saveNow,
    resetContent,
    restoreAvailable,
    restoreDraft,
    discardDraft,
  };
}
